import { trackAddToCart } from "../global/analytics/analytics";

// Serializes every cart write: Shopify doesn't apply concurrent cart requests
// in order, so rapid edits could otherwise land out of order.
let cartQueue = Promise.resolve();
let queuedCount = 0;

export function queueCartRequest(task) {
  queuedCount += 1;

  const run = cartQueue.then(task, task);

  cartQueue = run.catch(() => {});
  run.finally(() => (queuedCount -= 1)).catch(() => {});

  return run;
}

export function isCartBusy() {
  return queuedCount > 0;
}

// Resolves once every queued cart write (including any queued while waiting)
// has finished — checkout and cart links wait on this.
export async function whenCartIdle() {
  let tail;

  do {
    tail = cartQueue;
    await tail;
  } while (tail !== cartQueue);
}

// True while the request running now has another cart write queued behind it.
// Its response is about to be superseded, so it skips the (slow) section
// re-render and only the last request in a burst renders the cart.
function hasQueuedAfterCurrent() {
  return queuedCount > 1;
}

window.queueCartRequest = queueCartRequest;
window.isCartBusy = isCartBusy;
window.whenCartIdle = whenCartIdle;

async function readJson(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

// Line keys can change after any write (e.g. free-swatch repricing), so stale keys
// are re-resolved by variant + properties against the latest cart before sending.
let latestCart = null;
const knownLines = new Map();

function rememberCart(cart) {
  if (!Array.isArray(cart?.items)) return;

  latestCart = cart;

  cart.items.forEach((item) => {
    knownLines.set(item.key, {
      variantId: item.variant_id,
      properties: JSON.stringify(item.properties || {}),
    });
  });
}

function isLineKey(id) {
  return typeof id === "string" && id.includes(":");
}

function resolveLineKey(key, cart = latestCart) {
  if (!isLineKey(key) || !Array.isArray(cart?.items)) return key;
  if (cart.items.some((item) => item.key === key)) return key;

  // A key never seen in a response is still the page's own render, whose
  // "variantId:hash" format at least gives the variant.
  const known = knownLines.get(key);
  const variantId = known?.variantId ?? Number(key.split(":")[0]);
  const candidates = cart.items.filter((item) => item.variant_id === variantId);
  const sameLine = known && candidates.find((item) => JSON.stringify(item.properties || {}) === known.properties);

  if (sameLine) return sameLine.key;

  return candidates.length === 1 ? candidates[0].key : key;
}

// Re-resolves every line key a request carries — a change's `id`, or the keys
// of an update's `updates` map.
function resolveRequestKeys(body, cart = latestCart) {
  let resolved = body;

  if (isLineKey(body.id)) {
    resolved = { ...resolved, id: resolveLineKey(body.id, cart) };
  }

  if (body.updates && typeof body.updates === "object" && !Array.isArray(body.updates)) {
    resolved = {
      ...resolved,
      updates: Object.fromEntries(
        Object.entries(body.updates).map(([key, quantity]) => [resolveLineKey(key, cart), quantity]),
      ),
    };
  }

  return resolved;
}

function carriesLineKeys(body) {
  return isLineKey(body.id) || Object.keys(body.updates || {}).some(isLineKey);
}

async function fetchCart() {
  const cart = await fetch("/cart.js", { headers: { "Content-Type": "application/json" } }).then((response) =>
    response.json(),
  );

  rememberCart(cart);

  return cart;
}

function getCartSectionIds() {
  return [...document.querySelectorAll("[cart-section-id]")].map((el) => el.getAttribute("cart-section-id"));
}

function dispatchCartUpdate(cart, data) {
  rememberCart(cart);

  document.dispatchEvent(
    new CustomEvent("updateCart", {
      detail: { cart, data },
    }),
  );

  document.dispatchEvent(
    new CustomEvent("cart:updated", {
      detail: { cartData: cart },
    }),
  );
}

function postCart(url, body, options, isError) {
  const send = (requestBody, withSections) =>
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      // Section ids are read when the request actually runs, after anything
      // queued ahead of it has re-rendered the page.
      body: JSON.stringify(
        withSections ? { ...requestBody, sections: getCartSectionIds().join() } : requestBody,
      ),
    }).then(async (response) => ({ response, body: await readJson(response) }));

  return queueCartRequest(async () => {
    if (options?.onBeforeAdd) {
      options.onBeforeAdd();
    }

    try {
      const withSections = !hasQueuedAfterCurrent();
      let requestBody = resolveRequestKeys(body);
      let { response, body: responseBody } = await send(requestBody, withSections);

      // A key was still unknown (the cart changed somewhere this page never
      // saw, e.g. another tab) — re-read the cart and retry once if the lines
      // can be found under new keys.
      if (!response.ok && carriesLineKeys(body)) {
        const freshBody = resolveRequestKeys(body, await fetchCart());

        if (JSON.stringify(freshBody) !== JSON.stringify(requestBody)) {
          requestBody = freshBody;
          ({ response, body: responseBody } = await send(requestBody, withSections));
        }
      }

      // Any non-2xx (400 unknown line, 422 sold out, 409 conflict, 429
      // throttled, 5xx) is an error, so the caller can re-sync instead of
      // rendering a response with no cart or sections in it.
      if (!response.ok || isError(responseBody)) {
        if (options?.onError) {
          options.onError({ ...responseBody, httpStatus: response.status });
        }

        return;
      }

      dispatchCartUpdate(responseBody, options?.data);

      if (options?.onAfterAdd) {
        options.onAfterAdd(responseBody);
      }
    } catch (error) {
      console.error(error);

      if (options?.onError) {
        options.onError();
      }
    }
  });
}

window.changeCart = (change, options = {}) =>
  postCart("/cart/change.js", change, options, (response) => response.status === 422 || response.status === 409);

window.updateCart = (updates, options = {}) =>
  postCart(
    "/cart/update.js",
    updates,
    options,
    (response) => response.status === 422 && response.message === "Cart Error",
  );

window.updateCartAttributes = (attributes, options = {}) =>
  postCart("/cart/update.js", { attributes }, options, (response) => response.status === 422);

window.addToCart = (items, options = {}) => queueCartRequest(() => runAddToCart(items, options));

function runAddToCart(items, options) {
  if (options?.onBeforeAdd) {
    options.onBeforeAdd();
  }

  // Showroom styles carry a _showroom_data property so the showroom app can
  // mark the member as purchased (see components/showroom/showroom-button.js).
  const normalizedItems = (Array.isArray(items) ? items : [items]).map(
    (item) => window.applyShowroomAttribution?.(item) ?? item,
  );

  const data = {
    items: normalizedItems,
    // Skipped when another cart write is queued behind this one (see postCart)
    ...(hasQueuedAfterCurrent() ? {} : { sections: getCartSectionIds().join() }),
  };

  return fetch("/cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
    .then(async (response) => {
      const body = await readJson(response);

      if (!response.ok) {
        if (options?.onError) {
          options.onError(body);
        }

        return null;
      }

      return body;
    })
    .then((response) => {
      if (!response) return;

      const sections = response.sections;

      return fetch("/cart.js", {
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((cartResponse) => cartResponse.json())
        .then((cart) => {
          dispatchCartUpdate({ ...cart, sections }, options.data);

          // GA4 add_to_cart for every add on the site. Pass analytics: false
          // for adds that aren't a shopper adding a product.
          if (options?.analytics !== false) {
            trackAddToCart(normalizedItems, response.items, cart.currency);
          }

          if (options?.onAfterAdd) {
            options.onAfterAdd(response);
          }
        });
    })
    .catch((error) => {
      console.error("Unexpected add-to-cart error:", error);

      if (options?.onError) {
        options.onError({
          message: error instanceof Error ? error.message : "An unexpected cart error occurred.",
        });
      }
    });
}
